import React, { useEffect, useMemo, useState } from 'react';
import { RotateCw, X, Search, CheckCircle, AlertCircle, Users, Plus, Trash2 } from 'lucide-react';
import api from '../services/api';

interface ReabonnementModalProps {
    isOpen: boolean;
    mode: 'abonnement' | 'seance';
    /** Client à présélectionner (ex: depuis une ligne de la liste) */
    initialClient?: any | null;
    onClose: () => void;
    /** Appelé après succès avec les données prêtes pour le reçu */
    onSuccess: (receipt: any, client: any) => void;
    /** Prolonger un abonnement encore actif */
    onOpenProlonger?: (sub: any) => void;
}

const PAYMENT_METHODS = [
    { value: 'especes', label: 'Espèces' },
    { value: 'wave', label: 'Wave' },
    { value: 'orange_money', label: 'Orange Money' },
    { value: 'mtn_money', label: 'MTN Money' },
    { value: 'moov_money', label: 'Moov Money' },
    { value: 'carte_bancaire', label: 'Carte Bancaire' },
    { value: 'virement', label: 'Virement' },
];

const today = () => new Date().toISOString().split('T')[0];
const nowTime = () => new Date().toTimeString().substring(0, 5);
const fmt = (d?: string) => (d ? new Date(d).toLocaleDateString('fr-FR') : '-');

const addPeriod = (start: string, periodicity: string, q: number) => {
    const d = new Date(start);
    switch (periodicity) {
        case 'trimestriel': d.setMonth(d.getMonth() + 3 * q); break;
        case 'semestriel': d.setMonth(d.getMonth() + 6 * q); break;
        case 'annuel': d.setFullYear(d.getFullYear() + q); break;
        case 'hebdomadaire': d.setDate(d.getDate() + 7 * q); break;
        default: d.setMonth(d.getMonth() + q);
    }
    return d.toISOString().split('T')[0];
};

const ReabonnementModal: React.FC<ReabonnementModalProps> = ({ isOpen, mode, initialClient, onClose, onSuccess, onOpenProlonger }) => {
    const isAbo = mode === 'abonnement';
    const [clients, setClients] = useState<any[]>([]);
    const [forfaits, setForfaits] = useState<any[]>([]);
    const [coaches, setCoaches] = useState<any[]>([]);
    const [coachId, setCoachId] = useState('');
    const [search, setSearch] = useState('');
    const [client, setClient] = useState<any>(null);
    const [lastSub, setLastSub] = useState<any>(null);
    const [forfaitId, setForfaitId] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [startDate, setStartDate] = useState(today());
    const [quantity, setQuantity] = useState(1);
    const [sessionDate, setSessionDate] = useState(today());
    const [sessionTime, setSessionTime] = useState(nowTime());
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Chargement des clients et forfaits à l'ouverture
    useEffect(() => {
        if (!isOpen) return;
        setError('');
        setSearch('');
        setPaymentMethod('');
        setQuantity(1);
        setCoachId('');
        setStartDate(today());
        setSessionDate(today());
        setSessionTime(nowTime());
        setClient(initialClient || null);
        api.get('/clients').then(r => setClients(r.data)).catch(() => {});
        api.get('/coaches').then(r => setCoaches(r.data || [])).catch(() => {});
        api.get(isAbo ? '/forfaits/abonnements?active_only=true' : '/forfaits/seances?active_only=true')
            .then(r => {
                setForfaits(r.data);
                if (r.data.length > 0) setForfaitId(String(r.data[0].id));
            })
            .catch(() => {});
    }, [isOpen, isAbo, initialClient]);

    // Dernier abonnement du client sélectionné : permet de proposer une date de début logique
    useEffect(() => {
        setLastSub(null);
        if (!client || !isAbo) return;
        api.get(`/subscriptions/client/${client.id}`)
            .then(r => {
                const list: any[] = r.data || [];
                if (list.length === 0) return;
                const last = [...list].sort((a, b) => String(b.end_date).localeCompare(String(a.end_date)))[0];
                setLastSub(last);
                // Abonnement encore en cours : le nouveau démarre à la fin de l'actuel
                if (last.end_date && new Date(last.end_date) >= new Date(today())) {
                    setStartDate(String(last.end_date).substring(0, 10));
                }
            })
            .catch(() => {});
    }, [client, isAbo]);

    const suggestions = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (q.length < 2) return [];
        return clients
            .filter(c => `${c.last_name} ${c.first_name}`.toLowerCase().includes(q) || (c.phone && c.phone.includes(q)))
            .slice(0, 6);
    }, [search, clients]);

    const forfait = forfaits.find(f => String(f.id) === forfaitId);
    const isGroupPack = Boolean(
        isAbo && (
            (forfait?.max_members && Number(forfait.max_members) > 1) ||
            forfait?.name?.toLowerCase().includes('pack entreprise')
        )
    );
    const maxAdditionalAllowed = 4; // 1 souscripteur + 4 additionnels = 5 max
    const [additionalPackMembers, setAdditionalPackMembers] = useState<Array<{ full_name: string; phone: string }>>([]);

    const regFee = forfait?.registration_fee ? Number(forfait.registration_fee) : 0;
    const basePrice = forfait ? Number(forfait.price) * (isAbo ? Math.max(1, quantity) : 1) : 0;
    const total = basePrice + regFee;
    const endDate = isAbo && forfait && startDate ? addPeriod(startDate, forfait.periodicity, Math.max(1, quantity)) : '';
    const lastExpired = lastSub?.end_date && new Date(lastSub.end_date) < new Date(today());

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (!client) return setError('Veuillez sélectionner un client existant');
        if (!forfaitId) return setError('Veuillez sélectionner un forfait');
        if (!paymentMethod) return setError('Veuillez sélectionner un mode de paiement');
        if (forfait?.with_coach && !coachId) {
            return setError('Ce forfait requiert un coach : veuillez sélectionner le coach personnel');
        }
        if (isAbo && lastSub && !lastExpired) {
            return setError("L'abonnement de ce client est encore actif. Le réabonnement n'est disponible que pour les abonnements expirés. Utilisez la prolongation.");
        }

        try {
            setSubmitting(true);
            const label = PAYMENT_METHODS.find(m => m.value === paymentMethod)?.label || paymentMethod;
            const clientName = `${client.first_name} ${client.last_name}`;
            if (isAbo) {
                const cleanMembers = isGroupPack
                    ? [
                        {
                            full_name: `${client.first_name} ${client.last_name}`.trim(),
                            phone: (client.phone || '').trim(),
                            role: 'Souscripteur principal'
                        },
                        ...additionalPackMembers
                            .map(m => ({ full_name: m.full_name.trim(), phone: m.phone.trim() }))
                            .filter(m => m.full_name.length > 0)
                            .slice(0, maxAdditionalAllowed)
                    ]
                    : [];

                await api.post('/subscriptions/renouveler', {
                    client_id: client.id, forfait_id: Number(forfaitId), payment_method: paymentMethod,
                    start_date: startDate, quantity, coach_id: coachId ? Number(coachId) : null,
                    group_members: cleanMembers.length > 0 ? cleanMembers : null
                });
                onSuccess({
                    clientName, forfaitName: forfait?.name || 'Abonnement', periodicity: forfait?.periodicity || '',
                    duration: forfait?.duration || '', startDate: fmt(startDate), endDate: fmt(endDate),
                    quantity, paymentMethod: label, total
                }, client);
            } else {
                await api.post('/seances/renouveler', {
                    client_id: client.id, forfait_id: Number(forfaitId), payment_method: paymentMethod,
                    session_date: sessionDate, session_time: sessionTime, coach_id: coachId ? Number(coachId) : null
                });
                onSuccess({
                    clientName, forfaitName: forfait?.name || 'Séance', sessionDate: fmt(sessionDate),
                    sessionTime, paymentMethod: label, total
                }, client);
            }
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.error || 'Erreur lors du réabonnement');
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
    const labelCls = 'block text-xs font-bold text-gray-600 uppercase mb-1';

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[94vh] overflow-y-auto">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <h3 className="text-lg font-black text-gray-900 flex items-center gap-2">
                        <RotateCw size={18} className="text-blue-600" />
                        Réabonnement {isAbo ? 'Abonnement' : 'Séance'}
                    </h3>
                    <button type="button" onClick={onClose} className="p-1.5 rounded-full bg-gray-100 text-gray-500 hover:text-gray-800">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    {/* Client */}
                    <div>
                        <label className={labelCls}>Client existant</label>
                        {client ? (
                            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                                <div className="text-sm">
                                    <div className="font-bold text-gray-900">{client.last_name} {client.first_name}</div>
                                    <div className="text-xs text-gray-500 font-mono">{client.phone || '-'}</div>
                                </div>
                                <button type="button" onClick={() => { setClient(null); setLastSub(null); }} className="text-xs font-semibold text-blue-700 hover:underline">
                                    Changer
                                </button>
                            </div>
                        ) : (
                            <div className="relative">
                                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    autoFocus
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    placeholder="Rechercher par nom ou téléphone..."
                                    className={`${inputCls} pl-9`}
                                />
                                {suggestions.length > 0 && (
                                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
                                        {suggestions.map(c => (
                                            <button type="button" key={c.id} onClick={() => setClient(c)}
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex justify-between">
                                                <span className="font-semibold">{c.last_name} {c.first_name}</span>
                                                <span className="text-xs text-gray-500 font-mono">{c.phone}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Dernier abonnement */}
                    {isAbo && lastSub && (
                        <div className={`text-xs rounded-xl p-3 border ${lastExpired ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
                            <div className="font-semibold mb-1">
                                Dernier abonnement : <strong>{lastSub.forfait_name || 'Abonnement'}</strong> — {lastExpired ? 'expiré le' : 'valide jusqu\'au'} <strong>{fmt(lastSub.end_date)}</strong>
                            </div>
                            {!lastExpired && (
                                <div className="space-y-2 mt-2 pt-2 border-t border-amber-200/80">
                                    <p className="text-amber-800 font-medium">
                                        ⚠️ L'abonnement de ce client est encore <strong>actif</strong>. Le réabonnement n'est disponible que pour un abonnement expiré. Vous devez plutôt utiliser la prolongation pour augmenter sa durée.
                                    </p>
                                    {onOpenProlonger && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                onClose();
                                                onOpenProlonger(lastSub);
                                            }}
                                            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs shadow-xs transition-colors cursor-pointer"
                                        >
                                            Prolonger cet abonnement en cours
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Forfait */}
                    <div>
                        <label className={labelCls}>Forfait</label>
                        <select value={forfaitId} onChange={e => setForfaitId(e.target.value)} className={inputCls}>
                            {forfaits.map(f => (
                                <option key={f.id} value={f.id}>
                                    {f.name} — {Number(f.price).toLocaleString()} FCFA{f.periodicity ? ` (${f.periodicity})` : ''}{Number(f.registration_fee) > 0 ? ` + Inscription: ${Number(f.registration_fee).toLocaleString()} F` : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Coach personnel si forfait avec coach */}
                    {Boolean(forfait?.with_coach) && (
                        <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg space-y-1">
                            <label className="block text-xs font-bold text-amber-900 uppercase">
                                🏋️ Coach Personnel Attitré <span className="text-red-500">*</span>
                            </label>
                            <select
                                required
                                value={coachId}
                                onChange={e => setCoachId(e.target.value)}
                                className={inputCls}
                            >
                                <option value="">-- Sélectionner le coach --</option>
                                {coaches.map(c => (
                                    <option key={c.id} value={c.id}>
                                        {c.first_name} {c.last_name} {c.specialties ? `(${c.specialties})` : ''}
                                    </option>
                                ))}
                            </select>
                            <p className="text-[11px] text-amber-800">
                                Ce forfait requiert l'attribution d'un coach personnel.
                            </p>
                        </div>
                    )}

                    {/* Saisie Bénéficiaires si Pack Entreprise / Groupe */}
                    {isGroupPack && (
                        <div className="p-3.5 bg-purple-50 border border-purple-200 rounded-xl space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5 text-xs font-bold text-purple-900 uppercase">
                                    <Users size={15} className="text-purple-600" />
                                    <span>Bénéficiaires du Pack ({1 + additionalPackMembers.filter(m => m.full_name.trim()).length}/5 au total)</span>
                                </div>
                                {additionalPackMembers.length < maxAdditionalAllowed && (
                                    <button
                                        type="button"
                                        onClick={() => setAdditionalPackMembers([...additionalPackMembers, { full_name: '', phone: '' }])}
                                        className="px-2.5 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-[11px] font-bold flex items-center gap-1 cursor-pointer transition-colors"
                                    >
                                        <Plus size={12} />
                                        <span>Ajouter ({additionalPackMembers.length + 1}/4 autres)</span>
                                    </button>
                                )}
                            </div>

                            {/* Personne 1 : Souscripteur principal */}
                            <div className="flex items-center justify-between gap-2 bg-white p-2.5 rounded-lg border-2 border-purple-300/80 shadow-2xs">
                                <div className="flex items-center gap-2.5">
                                    <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-[11px] font-black flex items-center justify-center shrink-0">
                                        1
                                    </span>
                                    <div>
                                        <div className="text-xs font-bold text-gray-900">
                                            {client?.first_name} {client?.last_name}
                                        </div>
                                        <div className="text-[10px] text-gray-500 font-mono">
                                            {client?.phone || '-'}
                                        </div>
                                    </div>
                                </div>
                                <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-purple-100 text-purple-800">
                                    ✓ Souscripteur (inclus d'office)
                                </span>
                            </div>

                            {/* Personnes 2 à 5 */}
                            <div className="space-y-2">
                                {additionalPackMembers.length > 0 ? (
                                    additionalPackMembers.map((m, idx) => (
                                        <div key={idx} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-purple-100">
                                            <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-800 text-[11px] font-bold flex items-center justify-center shrink-0">
                                                {idx + 2}
                                            </span>
                                            <input
                                                type="text"
                                                placeholder={`Nom & Prénoms personne ${idx + 2} *`}
                                                value={m.full_name}
                                                onChange={(e) => {
                                                    const copy = [...additionalPackMembers];
                                                    copy[idx].full_name = e.target.value;
                                                    setAdditionalPackMembers(copy);
                                                }}
                                                className="flex-1 px-2.5 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-purple-400 outline-none font-semibold"
                                            />
                                            <input
                                                type="tel"
                                                placeholder="Téléphone"
                                                value={m.phone}
                                                onChange={(e) => {
                                                    const copy = [...additionalPackMembers];
                                                    copy[idx].phone = e.target.value;
                                                    setAdditionalPackMembers(copy);
                                                }}
                                                className="w-28 px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-purple-400 outline-none font-mono"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setAdditionalPackMembers(additionalPackMembers.filter((_, i) => i !== idx))}
                                                className="p-1 text-gray-400 hover:text-red-500 rounded"
                                                title="Supprimer cette personne"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    ))
                                ) : (
                                    <div className="p-2.5 bg-white/60 border border-dashed border-purple-200 rounded-lg text-center text-[11px] text-purple-800">
                                        Cliquez sur <strong>« Ajouter »</strong> pour saisir jusqu'à 4 autres personnes.
                                    </div>
                                )}
                            </div>
                            <p className="text-[10px] text-purple-700 italic">
                                1 seul badge partagé sera utilisé pour l'ensemble des membres de ce pack.
                            </p>
                        </div>
                    )}

                    {/* Frais d'inscription automatiques si configurés */}
                    {forfait && Number(forfait.registration_fee) > 0 && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-md flex items-center justify-between text-xs">
                            <span className="font-semibold text-gray-800">
                                Frais d'inscription obligatoire : <strong className="text-amber-700">+{Number(forfait.registration_fee).toLocaleString()} FCFA</strong>
                            </span>
                            <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Inclus</span>
                        </div>
                    )}

                    {isAbo ? (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Date de début</label>
                                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>Quantité</label>
                                <input type="number" min={1} value={quantity} onChange={e => setQuantity(Math.max(1, Number(e.target.value) || 1))} className={inputCls} />
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Date</label>
                                <input type="date" value={sessionDate} onChange={e => setSessionDate(e.target.value)} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>Heure</label>
                                <input type="time" value={sessionTime} onChange={e => setSessionTime(e.target.value)} className={inputCls} />
                            </div>
                        </div>
                    )}

                    {/* Paiement */}
                    <div>
                        <label className={labelCls}>Mode de paiement</label>
                        <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} className={inputCls}>
                            <option value="">-- Sélectionner --</option>
                            {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                    </div>

                    {/* Récapitulatif */}
                    <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm flex items-center justify-between">
                        <div className="text-xs text-slate-600">
                            {isAbo && endDate ? <>Fin prévue : <strong>{fmt(endDate)}</strong></> : 'Montant à payer'}
                        </div>
                        <div className="font-black text-gray-900">{total.toLocaleString()} FCFA</div>
                    </div>

                    {error && (
                        <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                            <AlertCircle size={14} /> {error}
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-md">
                        Annuler
                    </button>
                    <button type="submit" disabled={submitting || (isAbo && lastSub && !lastExpired)}
                        className="px-5 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-md flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed">
                        <CheckCircle size={16} /> {submitting ? 'Enregistrement...' : (isAbo && lastSub && !lastExpired) ? 'Réabonnement bloqué (Abonnement actif)' : 'Valider le réabonnement'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ReabonnementModal;

